import { useState } from 'react';
import { Loader2, Play, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { diagnosticsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

function fmt(n) {
  if (n == null) return '-';
  return Number(n).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function fmtInt(n) {
  if (n == null) return '-';
  return Number(n).toLocaleString('en-GB');
}

function fmtDate(iso) {
  if (!iso) return '-';
  try {
    return new Date(iso).toLocaleString('en-GB', {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  } catch {
    return iso;
  }
}

const TABS = [
  { key: 'sync', label: 'Sync State' },
  { key: 'probe', label: 'Live Probe' },
];

function StatCard({ label, value }) {
  return (
    <div className="rounded-lg border p-3">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="text-xl font-bold tabular-nums">{fmtInt(value)}</p>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Tab 1: Sync State                                                  */
/* ------------------------------------------------------------------ */

function SyncStateTab() {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    setLoading(true);
    setResult(null);
    try {
      const { data } = await diagnosticsAPI.brosSyncState();
      setResult(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      <Button onClick={run} disabled={loading}>
        {loading ? (
          <Loader2 className="h-4 w-4 animate-spin mr-2" />
        ) : (
          <Play className="h-4 w-4 mr-2" />
        )}
        Run
      </Button>

      {result && (
        <div className="space-y-6">
          {/* Summary stats */}
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            <StatCard label="Bookings" value={result.bookings_count} />
            <StatCard label="Invoices" value={result.invoices_count} />
            <StatCard label="Properties" value={result.properties_count} />
            <StatCard label="Revenue-bearing" value={result.revenue_bearing_count} />
            <StatCard label="Quarantined" value={result.quarantined_count} />
          </div>

          {/* Staleness indicator */}
          <div className="flex items-center gap-2 text-sm">
            {result.is_stale ? (
              <>
                <AlertTriangle className="h-4 w-4 text-amber-500" />
                <span className="text-amber-600 dark:text-amber-400">
                  Sync is stale (cutoff: {result.stale_cutoff_days} days). Last sync: {fmtDate(result.max_synced_at)}
                </span>
              </>
            ) : (
              <>
                <CheckCircle2 className="h-4 w-4 text-green-600" />
                <span className="text-green-700 dark:text-green-400">
                  Last sync: {fmtDate(result.max_synced_at)}
                </span>
              </>
            )}
          </div>

          {/* Quarantine reasons */}
          {result.quarantined_count > 0 && result.quarantine_reasons && result.quarantine_reasons.length > 0 && (
            <div className="space-y-2">
              <p className="text-sm font-medium flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-amber-500" />
                Quarantine reasons
              </p>
              <div className="overflow-x-auto border rounded-lg">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b bg-muted/50">
                      <th className="text-left px-2 py-1.5 font-medium">Reason</th>
                      <th className="text-right px-2 py-1.5 font-medium">Count</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.quarantine_reasons.map((r) => (
                      <tr key={r.reason} className="border-b last:border-0">
                        <td className="px-2 py-1 font-mono">{r.reason}</td>
                        <td className="px-2 py-1 text-right tabular-nums">{fmtInt(r.count)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* by_status table */}
          {result.by_status && result.by_status.length > 0 && (
            <div className="space-y-2">
              <p className="text-sm font-medium">By status</p>
              <div className="overflow-x-auto border rounded-lg">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b bg-muted/50">
                      <th className="text-left px-2 py-1.5 font-medium">Status</th>
                      <th className="text-right px-2 py-1.5 font-medium">Count</th>
                      <th className="text-right px-2 py-1.5 font-medium">Sum client_total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.by_status.map((row) => (
                      <tr key={row.status} className="border-b last:border-0">
                        <td className="px-2 py-1 font-mono">{row.status}</td>
                        <td className="px-2 py-1 text-right tabular-nums">{fmtInt(row.count)}</td>
                        <td className="px-2 py-1 text-right tabular-nums">{fmt(row.sum_client_total)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* by_ingestion table */}
          {result.by_ingestion && result.by_ingestion.length > 0 && (
            <div className="space-y-2">
              <p className="text-sm font-medium">By ingestion status</p>
              <div className="overflow-x-auto border rounded-lg">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b bg-muted/50">
                      <th className="text-left px-2 py-1.5 font-medium">Ingestion Status</th>
                      <th className="text-right px-2 py-1.5 font-medium">Count</th>
                      <th className="text-right px-2 py-1.5 font-medium">Sum client_total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.by_ingestion.map((row) => (
                      <tr key={row.ingestion_status} className="border-b last:border-0">
                        <td className="px-2 py-1 font-mono">{row.ingestion_status}</td>
                        <td className="px-2 py-1 text-right tabular-nums">{fmtInt(row.count)}</td>
                        <td className="px-2 py-1 text-right tabular-nums">{fmt(row.sum_client_total)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Tab 2: Live Probe                                                  */
/* ------------------------------------------------------------------ */

function LiveProbeTab() {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [showApiBookings, setShowApiBookings] = useState(false);

  const run = async () => {
    setLoading(true);
    setResult(null);
    setShowApiBookings(false);
    try {
      const { data } = await diagnosticsAPI.brosProbe();
      setResult(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      <Button onClick={run} disabled={loading}>
        {loading ? (
          <Loader2 className="h-4 w-4 animate-spin mr-2" />
        ) : (
          <Play className="h-4 w-4 mr-2" />
        )}
        Run
      </Button>

      {result && (
        <div className="space-y-6">
          {/* Error banner */}
          {result.had_error && (
            <div className="flex items-center gap-2 text-sm text-red-600 dark:text-red-400">
              <AlertTriangle className="h-4 w-4" />
              The probe encountered an error.
            </div>
          )}

          {/* Summary stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <StatCard label="API fetched" value={result.api_total_fetched} />
            <StatCard label="API recent" value={result.api_recent_count} />
            <StatCard label="DB count" value={result.db_count} />
            <div className="rounded-lg border p-3">
              <p className="text-xs text-muted-foreground">Gap</p>
              <p className={`text-xl font-bold tabular-nums ${
                result.gap > 0 ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'
              }`}>
                {fmtInt(result.gap)}
              </p>
            </div>
          </div>

          <p className="text-xs text-muted-foreground">
            Probe window: from {result.from_date || '-'}
          </p>

          {/* Missing in DB */}
          {result.missing_in_db_count > 0 && (
            <div className="space-y-2">
              <p className="text-sm font-medium flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-red-500" />
                Missing in DB ({result.missing_in_db_count})
              </p>
              <div className="flex flex-wrap gap-1">
                {result.missing_in_db_ids.map((id) => (
                  <Badge key={id} variant="destructive" className="font-mono text-xs">
                    {id}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {/* Missing in API */}
          {result.missing_in_api_count > 0 && (
            <div className="space-y-2">
              <p className="text-sm font-medium flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-amber-500" />
                Missing in API ({result.missing_in_api_count})
              </p>
              <div className="flex flex-wrap gap-1">
                {result.missing_in_api_ids.map((id) => (
                  <Badge key={id} variant="outline" className="font-mono text-xs border-amber-400 text-amber-600 dark:text-amber-400">
                    {id}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {/* All-clear if no gaps */}
          {result.missing_in_db_count === 0 && result.missing_in_api_count === 0 && !result.had_error && (
            <div className="flex items-center gap-2 text-sm text-green-600 dark:text-green-400">
              <CheckCircle2 className="h-4 w-4" />
              API and DB are in sync for the probe window.
            </div>
          )}

          {/* Collapsible API bookings table */}
          {result.api_bookings && result.api_bookings.length > 0 && (
            <div className="space-y-2">
              <button
                type="button"
                onClick={() => setShowApiBookings(!showApiBookings)}
                className="text-sm font-medium text-muted-foreground cursor-pointer hover:text-foreground"
              >
                {showApiBookings ? 'Hide' : 'Show'} API bookings ({result.api_bookings.length})
              </button>
              {showApiBookings && (
                <div className="overflow-x-auto border rounded-lg max-h-80 overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="sticky top-0">
                      <tr className="border-b bg-muted/50">
                        <th className="text-left px-2 py-1.5 font-medium">bros_id</th>
                        <th className="text-left px-2 py-1.5 font-medium">Client</th>
                        <th className="text-left px-2 py-1.5 font-medium">Company</th>
                        <th className="text-left px-2 py-1.5 font-medium">Check-in</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.api_bookings.map((b) => (
                        <tr key={b.bros_id} className="border-b last:border-0">
                          <td className="px-2 py-1 font-mono">{b.bros_id}</td>
                          <td className="px-2 py-1">{b.client_name || '-'}</td>
                          <td className="px-2 py-1">{b.company || '-'}</td>
                          <td className="px-2 py-1">{b.check_in || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Main panel                                                         */
/* ------------------------------------------------------------------ */

export function BROSSyncHealthPanel() {
  const [activeTab, setActiveTab] = useState('sync');

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">BR OS Sync Health</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-xs text-muted-foreground">
          Sync pipeline health: collection stats, staleness, and live API-vs-DB probe.
        </p>

        {/* Tab row */}
        <div className="flex gap-1">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              type="button"
              onClick={() => setActiveTab(tab.key)}
              className={`px-3 py-1.5 text-sm rounded-md font-medium transition-colors ${
                activeTab === tab.key
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted hover:bg-muted/80'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {activeTab === 'sync' && <SyncStateTab />}
        {activeTab === 'probe' && <LiveProbeTab />}
      </CardContent>
    </Card>
  );
}
